import { ShieldAlert, History, Lock, Download, Shield, ShieldCheck, KeyRound, Radio } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const securityPillars = [
  { icon: ShieldAlert, title: 'PHI Isolation & Data Hardening', body: 'All Protected Health Information (PHI) is encrypted at rest (AES-256) and in transit (TLS 1.2+). Database access is restricted via strict Row Level Security (RLS) ensuring total tenant isolation.' },
  { icon: History, title: 'Immutable Audit Logging', body: 'Every system interaction, export, and PHI access event is recorded in an immutable, append-only audit log. These logs are tamper-proof and provide a permanent record for institutional compliance officers.' },
  { icon: Lock, title: 'Secure Document Lifecycle', body: 'Documents and operative notes are stored in private storage buckets. Access is granted only via time-limited, 5-minute signed URLs that expire automatically after the surgical review is complete.' },
];

const trustBadges = [
  { icon: ShieldCheck, label: 'HIPAA Compliant Architecture' },
  { icon: KeyRound, label: 'AES-256 Data Encryption' },
  { icon: Lock, label: 'TLS 1.2+ Transit Security' },
  { icon: Radio, label: 'Audit-Immutable Logging' },
];

export function SecurityTrustSection() {
  return (
    <section id="security-trust" className="py-20 md:py-28 bg-secondary">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-14 md:mb-16 max-w-3xl mx-auto">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
            Security & Compliance
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
            Enterprise-Grade Security & HIPAA Compliance
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            LINK is designed to meet the rigorous security requirements of the nation's
            largest health systems. Your data is protected by the same standards we used
            at the Ohio Governor's Advisory Board.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {securityPillars.map((pillar) => (
            <Card key={pillar.title} className="bg-card border border-border rounded-xl transition-all hover:shadow-lg">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 border border-primary/20">
                  <pillar.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-foreground">{pillar.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{pillar.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Security Whitepaper CTA */}
        <div className="mt-10 md:mt-14 max-w-5xl mx-auto">
          <Card className="bg-card border border-primary/20 rounded-xl">
            <CardContent className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                <ShieldCheck className="h-7 w-7 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="text-xl md:text-2xl font-bold text-foreground">
                  Enterprise Security Architecture & HIPAA Compliance
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-2xl">
                  Download the complete technical whitepaper on LINK's data isolation,
                  encryption standards, and SOC 2 Type II roadmap.
                </p>
              </div>
              <Button className="bg-cta hover:bg-cta/90 text-cta-foreground shrink-0 gap-2 font-bold rounded-md">
                <Download className="h-4 w-4" />
                Download Security Whitepaper (PDF)
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 md:mt-10 max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trustBadges.map((badge) => (
              <div key={badge.label} className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-border bg-card">
                <badge.icon className="h-4 w-4 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-xs md:text-sm font-semibold text-foreground tracking-tight leading-tight">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
